import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  LogOut,
  Radio,
  Youtube,
  Music,
  Sparkles,
  Lightbulb,
  Home as HomeIcon,
  CalendarDays,
  Film,
  Gamepad2,
  Cpu,
  Mail,
  Vote as VoteIcon,
  Paintbrush,
  ShieldCheck,
  Coins,
  Disc3,
  MessagesSquare,
  User as UserIcon
} from 'lucide-react';
import { useActiveSection } from '../../hooks/useActiveSection';
import { useAdminPendingCounts } from '../../hooks/useAdminPendingCounts';
import { useAuth } from '../../context/AuthContext';

export const NAV_LINKS = [
  { label: 'Home', href: '/#home', Icon: HomeIcon },
  { label: 'Schema', href: '/#schedule', Icon: CalendarDays },
  { label: 'Streams', href: '/#vods', Icon: Film },
  { label: 'Games', href: '/#games', Icon: Gamepad2 },
  { label: 'YouTube', href: '/#youtube', Icon: Youtube },
  { label: 'Setup', href: '/setup', Icon: Cpu },
  { label: 'Forum', href: '/forum', Icon: MessagesSquare },
  { label: 'Contact', href: '/#contact-form', spy: 'contact', Icon: Mail }
];

export const STREAM_LINKS = [
  { label: 'Alle mogelijkheden', href: '/hoe-suggereren', highlight: 'howto' },
  { label: 'Game-suggesties', href: '/vote', highlight: 'vote' },
  { label: 'Video-suggesties', href: '/kijk', highlight: 'watch' },
  { label: 'Muziek-suggesties', href: '/muziek', highlight: 'music' },
  { label: 'Tekenen op stream', href: '/tekenen', highlight: 'draw' },
  { label: 'Kanaalpunten', href: '/kanaalpunten', highlight: 'points' },
  { label: 'Polls', href: '/polls', highlight: 'poll' }
];

function NavLinkItem({ link, className, onClick, children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isHash = link.href.includes('#');

  if (!isHash) {
    return (
      <Link to={link.href} onClick={onClick} className={className}>
        {children}
      </Link>
    );
  }

  const hash = link.href.substring(link.href.indexOf('#'));
  const handleClick = (e) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      navigate('/' + hash);
    } else {
      const id = hash.slice(1);
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.replaceState(null, '', hash);
    }
    if (onClick) onClick();
  };

  return (
    <a href={link.href} onClick={handleClick} className={className}>
      {children}
    </a>
  );
}

function StreamDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="nav-stream-dropdown"
        className="px-3 py-2 rounded-md text-sm font-extrabold text-red-400 hover:text-red-300 border border-red-500/50 hover:border-red-400 hover:bg-red-600/10 transition-colors inline-flex items-center gap-1.5 cursor-pointer"
      >
        <Lightbulb className="w-4 h-4" />
        Stream items
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="absolute top-full left-0 pt-1 min-w-[220px]">
          <div className="rounded-xl bg-zinc-900/95 backdrop-blur-xl border border-white/10 shadow-xl overflow-hidden py-1 whitespace-nowrap">
          {STREAM_LINKS.map((l) => {
            let cls = 'text-zinc-200';
            let Icon = Gamepad2;
            if (l.highlight === 'howto') {
              cls = 'text-white hover:bg-white/10 border-b border-white/10 mb-1';
              Icon = Lightbulb;
            } else if (l.highlight === 'vote') {
              cls = 'text-violet-300 hover:text-white hover:bg-violet-600/20';
              Icon = Gamepad2;
            } else if (l.highlight === 'watch') {
              cls = 'text-red-300 hover:text-white hover:bg-red-600/20';
              Icon = Youtube;
            } else if (l.highlight === 'music') {
              cls = 'text-emerald-300 hover:text-white hover:bg-emerald-600/20';
              Icon = Music;
            } else if (l.highlight === 'forum') {
              cls = 'text-sky-300 hover:text-white hover:bg-sky-600/20';
              Icon = MessagesSquare;
            } else if (l.highlight === 'poll') {
              cls = 'text-amber-300 hover:text-white hover:bg-amber-600/20';
              Icon = VoteIcon;
            } else if (l.highlight === 'draw') {
              cls = 'text-pink-300 hover:text-white hover:bg-pink-600/20';
              Icon = Paintbrush;
            } else if (l.highlight === 'points') {
              cls = 'text-purple-300 hover:text-white hover:bg-purple-600/20';
              Icon = Coins;
            }
            return (
              <NavLinkItem
                key={l.href}
                link={l}
                onClick={() => setOpen(false)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold transition-colors ${cls}`}
              >
                <Icon className="w-3.5 h-3.5" /> {l.label}
              </NavLinkItem>
            );
          })}
          </div>
        </div>
      )}
    </div>
  );
}

export function DesktopLinks() {
  const location = useLocation();
  const hashIds = useMemo(
    () =>
      NAV_LINKS.filter((l) => l.href.includes('#')).map((l) =>
        l.spy || l.href.substring(l.href.indexOf('#') + 1)
      ),
    []
  );
  const activeId = useActiveSection(hashIds, location.pathname === '/');

  return (
    <nav className="hidden md:flex items-center gap-1">
      {NAV_LINKS.map((l) => {
        const Icon = l.Icon;
        const isHash = l.href.includes('#');
        const hashId = isHash ? l.spy || l.href.substring(l.href.indexOf('#') + 1) : '';
        const isActive = isHash
          ? location.pathname === '/' && activeId === hashId
          : location.pathname === l.href;

        const base = 'px-3 py-2 rounded-md text-sm transition-colors inline-flex items-center gap-1.5 cursor-pointer relative';
        const cls = isActive
          ? 'text-white bg-violet-600/15'
          : 'text-zinc-300 hover:text-white hover:bg-white/5';

        const node = (
          <NavLinkItem
            key={l.href}
            link={l}
            className={`${base} ${cls}`}
          >
            {Icon && (
              <Icon
                className={`w-4 h-4 ${isActive ? 'text-violet-300' : 'opacity-80'}`}
              />
            )}
            {l.label}
          </NavLinkItem>
        );
        // After Contact, inject "Stream suggesties" dropdown. The Inloggen
        // button is rendered separately in Navbar so it sits flush with the
        // right edge of the page.
        if (l.label === 'Contact') {
          return (
            <React.Fragment key={l.href}>
              {node}
              <StreamDropdown />
            </React.Fragment>
          );
        }
        return node;
      })}
    </nav>
  );
}

export function LiveStatusBadge({ isLive, twitchUrl }) {
  if (isLive) {
    return (
      <a
        href={twitchUrl}
        target="_blank"
        rel="noreferrer"
        className="flex items-center gap-2 px-3 py-1.5 bg-red-500/15 border border-red-500/40 text-red-300 rounded-full text-xs font-semibold"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" /> LIVE
      </a>
    );
  }
  return (
    <span className="flex items-center gap-2 px-3 py-1.5 bg-zinc-800/60 border border-white/10 text-zinc-400 rounded-full text-xs font-medium">
      <Radio className="w-3 h-3" /> Offline
    </span>
  );
}

export function UserMenu({ user, menuOpen, onToggle, onLogout }) {
  const { counts, justIncreased } = useAdminPendingCounts();
  const total = counts?.total || 0;
  const showBadge = user.is_broadcaster && total > 0;

  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className={`relative flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-white/5 hover:bg-white/10 border transition-colors ${
          justIncreased && showBadge
            ? 'border-red-400/70 shadow-[0_0_18px_rgba(248,113,113,0.45)]'
            : 'border-white/10'
        }`}
        data-testid="user-pill"
      >
        {user.avatar ? (
          <img
            src={user.avatar}
            alt={user.display_name}
            className="w-7 h-7 rounded-full object-cover"
          />
        ) : (
          <div
            className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500"
            aria-label={user.display_name}
          />
        )}
        <span className="text-sm text-white">{user.display_name}</span>
        {showBadge && (
          <span
            className="absolute -top-1 -right-1 inline-flex items-center justify-center"
            data-testid="user-pill-badge"
            title={`${total} nieuwe inzendingen`}
          >
            {justIncreased && (
              <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-75" />
            )}
            <span className="relative min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 border-2 border-zinc-950 text-[10px] font-bold text-white flex items-center justify-center">
              {total > 9 ? '9+' : total}
            </span>
          </span>
        )}
      </button>
      {menuOpen && (
        <div
          className="absolute right-0 mt-2 w-60 rounded-xl bg-zinc-900 border border-white/10 shadow-xl overflow-hidden"
          onMouseLeave={onToggle}
        >
          <div className="px-4 py-3 border-b border-white/5">
            <div className="text-sm font-semibold text-white">{user.display_name}</div>
            <div className="text-xs text-zinc-400">@{user.login}</div>
          </div>
          {(user.is_broadcaster || user.is_channel_mod) && (
            <Link
              to="/admin"
              onClick={onToggle}
              data-testid="user-menu-admin"
              className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-sm text-violet-200 hover:text-white hover:bg-violet-600/15 border-b border-white/5"
            >
              <span className="inline-flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-violet-300" /> {user.is_broadcaster ? 'Admin dashboard' : 'Mod dashboard'}
              </span>
              {total > 0 && (
                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500/90 text-[10px] font-bold text-white">
                  {total > 9 ? '9+' : total}
                </span>
              )}
            </Link>
          )}
          {user.is_broadcaster && (
            <Link
              to="/grijpmachine"
              onClick={onToggle}
              data-testid="user-menu-grijpmachine"
              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-fuchsia-200 hover:text-white hover:bg-fuchsia-600/15 border-b border-white/5"
            >
              <Sparkles className="w-4 h-4 text-fuchsia-300" /> Grijpmachine
            </Link>
          )}
          {user.is_broadcaster && (
            <Link
              to="/slotmachine"
              onClick={onToggle}
              data-testid="user-menu-slotmachine"
              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-amber-200 hover:text-white hover:bg-amber-600/15 border-b border-white/5"
            >
              <Coins className="w-4 h-4 text-amber-300" /> Slotmachine
            </Link>
          )}
          {user.is_broadcaster && (
            <Link
              to="/rad"
              onClick={onToggle}
              data-testid="user-menu-rad"
              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-teal-200 hover:text-white hover:bg-teal-600/15 border-b border-white/5"
            >
              <Disc3 className="w-4 h-4 text-teal-300" /> Rad van fortuin
            </Link>
          )}
          <Link
            to="/profiel"
            onClick={onToggle}
            data-testid="user-menu-profile"
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-zinc-200 hover:bg-white/5 border-b border-white/5"
          >
            <UserIcon className="w-4 h-4 text-zinc-400" /> Mijn inzendingen
          </Link>
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-zinc-200 hover:bg-white/5"
          >
            <LogOut className="w-4 h-4" /> Uitloggen
          </button>
        </div>
      )}
    </div>
  );
}

export function LoginButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid="nav-login-btn"
      className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold transition-colors"
    >
      Inloggen
    </button>
  );
}

export function MobileMenu({ open, onClose, user, onLogin, onLogout }) {
  if (!open) return null;
  return (
    <div className="md:hidden bg-zinc-950/95 backdrop-blur-xl border-t border-white/5">
      <div className="px-6 py-4 flex flex-col gap-1">
        {NAV_LINKS.map((l) => {
          const cls = 'text-zinc-300 hover:text-white';
          const Icon = l.Icon;
          // After Contact, render "Wat gaan we streamen?" section
          if (l.label === 'Contact') {
            return (
              <React.Fragment key={l.href}>
                <NavLinkItem
                  link={l}
                  onClick={onClose}
                  className={`py-2 cursor-pointer inline-flex items-center gap-2 ${cls}`}
                >
                  {Icon && <Icon className="w-4 h-4 opacity-80" />}
                  {l.label}
                </NavLinkItem>
                <div className="pt-3 mt-1 border-t border-white/10">
                  <div className="text-red-400 font-extrabold text-sm py-2 inline-flex items-center gap-1.5">
                    <Lightbulb className="w-4 h-4" />
                    Stream items
                  </div>
                  {STREAM_LINKS.map((s) => {
                    let scls = 'text-zinc-300';
                    if (s.highlight === 'howto') scls = 'text-white font-semibold';
                    else if (s.highlight === 'vote') scls = 'text-violet-300 font-semibold';
                    else if (s.highlight === 'watch') scls = 'text-red-300 font-semibold';
                    else if (s.highlight === 'music') scls = 'text-emerald-300 font-semibold';
                    else if (s.highlight === 'forum') scls = 'text-sky-300 font-semibold';
                    else if (s.highlight === 'poll') scls = 'text-amber-300 font-semibold';
                    else if (s.highlight === 'draw') scls = 'text-pink-300 font-semibold';
                    else if (s.highlight === 'points') scls = 'text-purple-300 font-semibold';
                    return (
                      <NavLinkItem
                        key={s.href}
                        link={s}
                        onClick={onClose}
                        className={`py-2 pl-4 cursor-pointer block ${scls}`}
                      >
                        {s.label}
                      </NavLinkItem>
                    );
                  })}
                </div>
              </React.Fragment>
            );
          }
          return (
            <NavLinkItem
              key={l.href}
              link={l}
              onClick={onClose}
              className={`py-2 cursor-pointer inline-flex items-center gap-2 ${cls}`}
            >
              {Icon && <Icon className="w-4 h-4 opacity-80" />}
              {l.label}
            </NavLinkItem>
          );
        })}
        {user ? (
          <>
            {(user.is_broadcaster || user.is_channel_mod) && (
              <Link
                to="/admin"
                onClick={onClose}
                className="mt-2 inline-flex items-center justify-center gap-2 py-2 rounded-md bg-violet-600/15 border border-violet-500/40 text-violet-200 text-sm font-semibold"
              >
                <ShieldCheck className="w-4 h-4" /> {user.is_broadcaster ? 'Admin dashboard' : 'Mod dashboard'}
              </Link>
            )}
            {user.is_broadcaster && (
              <Link
                to="/grijpmachine"
                onClick={onClose}
                className="mt-2 inline-flex items-center justify-center gap-2 py-2 rounded-md bg-fuchsia-600/15 border border-fuchsia-500/40 text-fuchsia-200 text-sm font-semibold"
              >
                <Sparkles className="w-4 h-4" /> Grijpmachine
              </Link>
            )}
            {user.is_broadcaster && (
              <Link
                to="/slotmachine"
                onClick={onClose}
                className="mt-2 inline-flex items-center justify-center gap-2 py-2 rounded-md bg-amber-600/15 border border-amber-500/40 text-amber-200 text-sm font-semibold"
              >
                <Coins className="w-4 h-4" /> Slotmachine
              </Link>
            )}
            {user.is_broadcaster && (
              <Link
                to="/rad"
                onClick={onClose}
                className="mt-2 inline-flex items-center justify-center gap-2 py-2 rounded-md bg-teal-600/15 border border-teal-500/40 text-teal-200 text-sm font-semibold"
              >
                <Disc3 className="w-4 h-4" /> Rad van fortuin
              </Link>
            )}
            <button
              onClick={async () => {
                onClose();
                await onLogout();
              }}
              className="mt-2 text-center py-2 rounded-md bg-white/5 border border-white/10 text-white text-sm font-semibold"
            >
              Uitloggen ({user.display_name})
            </button>
          </>
        ) : (
          <button
            onClick={() => {
              onClose();
              onLogin();
            }}
            data-testid="nav-mobile-login-btn"
            className="mt-2 inline-flex items-center justify-center gap-2 py-2 rounded-md bg-violet-600 text-white text-sm font-semibold"
          >
            Inloggen
          </button>
        )}
      </div>
    </div>
  );
}
